import { useState, useEffect, useRef } from "react";
import { MessageSquare, Hash, Send, Users, Search, ArrowLeft, Paperclip, UserCircle, ChevronRight, Bot, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { API_URL } from "@/config/api";
import { authFetch } from "@/utils/auth-fetch";
import { io, Socket } from 'socket.io-client';

interface Channel {
  id: string;
  channelId: string;
  name: string;
  emoji: string;
  purpose: string;
  unreadCount?: number;
  lastMessage?: string;
  lastSender?: string;
  lastActive?: string;
}

interface Agent {
  id: string;
  name: string;
  avatar: string;
  status: 'online' | 'offline' | 'busy';
  role: string;
  lastMessage?: string;
  unreadCount?: number;
  lastActive?: string;
}

interface Message {
  id: string;
  agentName: string;
  messageType: string;
  text: string;
  timestamp: string;
  reactions: Record<string, boolean>;
  threadId?: string;
  isUser?: boolean;
}

interface ChatMessage {
  id: string;
  sender: string;
  avatarSrc: string;
  message: string;
  timestamp: string;
  isUser: boolean;
}

const agentAvatars: Record<string, string> = {
  'site-crawler': '/crew-agents/crawler.jpg',
  'topic-explorer': '/crew-agents/adjacent.jpg',
  'chatbot': '/crew-agents/chatbot.jpg',
  'voicebot': '/crew-agents/voicebot.jpg',
  'research-agent': '/crew-agents/topic-research.jpg',
  'behavior-analyst': '/crew-agents/behavior-analyst.jpg',
  'growth-analyst': '/crew-agents/growth-analyst.jpg',
  'web-analyst': '/crew-agents/webanalytics.png',
  'competitors-analyst': '/crew-agents/competitors.png',
  'replay-analyst': '/crew-agents/replay-analyst.jpg'
};

// Sample agents data
const sampleAgents: Agent[] = [
  {
    id: 'chatbot',
    name: 'ChatBot',
    avatar: '/crew-agents/chatbot.jpg',
    status: 'online',
    role: 'Customer Support',
    lastMessage: 'Customer asking about pricing plans',
    unreadCount: 2,
    lastActive: '2 minutes ago'
  },
  {
    id: 'content-bot',
    name: 'ContentBot',
    avatar: '/crew-agents/content-generator.jpg',
    status: 'online',
    role: 'Content Analyzer',
    lastMessage: 'Found trending topic opportunities',
    unreadCount: 0,
    lastActive: '10 minutes ago'
  },
  {
    id: 'competitor-bot',
    name: 'CompetitorBot',
    avatar: '/crew-agents/competitors.png',
    status: 'busy',
    role: 'Market Intelligence',
    lastMessage: 'Competitor analysis complete',
    unreadCount: 1,
    lastActive: '1 hour ago'
  },
  {
    id: 'growth-agent',
    name: 'GrowthAgent',
    avatar: '/crew-agents/growth-analyst.jpg',
    status: 'online',
    role: 'Growth Optimization',
    lastMessage: 'Conversion rate insights ready',
    unreadCount: 3,
    lastActive: '30 minutes ago'
  }
];

const ChatMessageComponent = ({ message, isUser }: { message: any; isUser?: boolean }) => {
  const messageIsUser = isUser || message.isUser || message.agentName === 'user';
  
  return (
    <div className={`flex gap-3 mb-4 ${messageIsUser ? 'justify-end' : ''}`}>
      {!messageIsUser && (
        <Avatar className="h-8 w-8 mt-1">
          <AvatarImage src={agentAvatars[message.agentName] || '/crew-agents/default-agent.jpg'} />
          <AvatarFallback>
            {message.agentName?.slice(0, 2).toUpperCase() || 'AI'}
          </AvatarFallback>
        </Avatar>
      )}
      
      <div className={`max-w-[70%] ${messageIsUser ? 'bg-primary text-primary-foreground' : 'bg-muted'} rounded-lg px-4 py-2`}>
        <div className="flex justify-between items-baseline mb-1">
          <span className={`font-medium text-sm ${messageIsUser ? 'text-primary-foreground' : 'text-foreground'}`}>
            {messageIsUser ? 'You' : formatAgentName(message.agentName)}
          </span>
          <span className={`text-xs ml-4 ${messageIsUser ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
            {format(new Date(message.timestamp), 'h:mm a')}
          </span>
        </div>
        <p className={`text-sm whitespace-pre-line ${messageIsUser ? 'text-primary-foreground' : 'text-foreground'}`}>
          {message.text || message.message}
        </p>
      </div>
      
      {messageIsUser && (
        <Avatar className="h-8 w-8 mt-1">
          <AvatarImage src="/crew-agents/user-avatar.jpg" />
          <AvatarFallback><UserCircle className="h-4 w-4" /></AvatarFallback>
        </Avatar>
      )}
    </div>
  );
};

const formatAgentName = (agentName: string) => {
  return agentName
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'online': return 'bg-green-500';
    case 'busy': return 'bg-yellow-500';
    case 'offline': return 'bg-gray-400';
    default: return 'bg-gray-400';
  }
};

const AgentInsightsHub = () => {
  const [view, setView] = useState<'overview' | 'channel' | 'agent'>('overview');
  const [selectedChannel, setSelectedChannel] = useState<string>('');
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [channels, setChannels] = useState<Channel[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [channelInfo, setChannelInfo] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const userId = localStorage.getItem('user_id') || 'anonymous';

  // Fetch channels on mount and set up auto-refresh
  useEffect(() => {
    fetchChannels();
    
    // Auto-refresh channels every 30 seconds
    const channelInterval = setInterval(fetchChannels, 30000);
    
    return () => clearInterval(channelInterval);
  }, []);

  // Auto-refresh messages when viewing a channel
  useEffect(() => {
    if (view === 'channel' && selectedChannel) {
      fetchMessages(selectedChannel);
      
      // Auto-refresh messages every 10 seconds
      const messageInterval = setInterval(() => {
        fetchMessages(selectedChannel);
      }, 10000);
      
      return () => clearInterval(messageInterval);
    }
  }, [view, selectedChannel]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchChannels = async () => {
    try {
      const response = await fetch(`${API_URL}/conductor/channels`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
      
      const data = await response.json();
      if (data.success) {
        // Fetch real message counts for each channel
        const channelsWithActivity = await Promise.all(
          data.data.map(async (channel: Channel) => {
            try {
              // IMPORTANT: conductor expects the channel slug (channelId), not the UUID id
              const channelKey = channel.channelId || channel.id;
              const messagesResponse = await fetch(`${API_URL}/conductor/channels/${channelKey}/messages?limit=1`, {
                headers: {
                  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                  'Content-Type': 'application/json',
                },
                credentials: 'include'
              });
              
              const messagesData = await messagesResponse.json();
              const messages = messagesData.success && messagesData.data && messagesData.data.messages 
                ? messagesData.data.messages 
                : [];
              const lastMessage = messages.length > 0 ? messages[0] : null;
              
              return {
                ...channel,
                unreadCount: messages.length,
                lastMessage: lastMessage ? lastMessage.text : 'No messages yet',
                lastSender: lastMessage ? formatAgentName(lastMessage.agentName) : '',
                lastActive: lastMessage ? new Date(lastMessage.timestamp).toLocaleString() : 'Never'
              };
            } catch (error) {
              console.error(`Failed to fetch messages for channel ${channel.id}:`, error);
              return {
                ...channel,
                unreadCount: 0,
                lastMessage: 'No messages yet',
                lastSender: '',
                lastActive: 'Never'
              };
            }
          })
        );
        setChannels(channelsWithActivity);
      }
    } catch (error) {
      console.error('Failed to fetch channels:', error);
    }
  };

  const fetchMessages = async (channelId: string) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/conductor/channels/${channelId}/messages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
      
      const data = await response.json();
      if (data.success) {
        setMessages(data.data.messages);
        setChannelInfo(data.data.channel);
      }
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChannelClick = (channelId: string) => {
    setSelectedChannel(channelId);
    setView('channel');
    fetchMessages(channelId);
  };

  const handleAgentClick = (agentId: string) => {
    setSelectedAgent(agentId);
    setView('agent');
    // In a real app, this would fetch direct messages with the agent
    setMessages([]);
    setChannelInfo(sampleAgents.find(a => a.id === agentId));
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      if (view === 'channel' && selectedChannel) {
        const response = await fetch(`${API_URL}/conductor/messages/reply`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            text: messageInput,
            userId: userId,
            channelId: selectedChannel
          })
        });
        
        if (response.ok) {
          setMessageInput('');
          fetchMessages(selectedChannel);
        }
      } else if (view === 'agent') {
        // Add user message locally for direct messages
        const userMessage = {
          id: `user-${Date.now()}`,
          agentName: 'user',
          messageType: 'message',
          text: messageInput,
          timestamp: new Date().toISOString(),
          reactions: {},
          isUser: true
        };
        setMessages(prev => [...prev, userMessage]);
        setMessageInput('');
        
        // Simulate agent response
        setTimeout(() => {
          const agentResponse = {
            id: `agent-${Date.now()}`,
            agentName: selectedAgent,
            messageType: 'response',
            text: 'I\'ll analyze this request and get back to you with insights.',
            timestamp: new Date().toISOString(),
            reactions: {},
            isUser: false
          };
          setMessages(prev => [...prev, agentResponse]);
        }, 1000);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSendingMessage(false);
    }
  };

  const goBack = () => {
    setView('overview');
    setSelectedChannel('');
    setSelectedAgent('');
    setMessages([]);
    setChannelInfo(null);
  };

  const filteredChannels = channels.filter(channel =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredAgents = sampleAgents.filter(agent =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (view === 'overview') {
    return (
      <div className="container mx-auto p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <MessageSquare className="mr-2 h-6 w-6 text-primary" />
              Agent Insights Hub
            </h1>
            <p className="text-muted-foreground">
              Communicate with AI agents and monitor team channels
            </p>
          </div>
          
          <div className="mt-4 md:mt-0 relative w-full md:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
            <Input 
              type="text" 
              placeholder="Search agents or channels..." 
              className="pl-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Direct Messages Section */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <UserCircle className="mr-2 h-5 w-5" />
            Direct Messages
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredAgents.map((agent) => (
              <Card 
                key={agent.id} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${agent.unreadCount > 0 ? 'border-primary/50' : ''}`}
                onClick={() => handleAgentClick(agent.id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={agent.avatar} />
                          <AvatarFallback>{agent.name.slice(0, 2)}</AvatarFallback>
                        </Avatar>
                        <div className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-background ${getStatusColor(agent.status)}`} />
                      </div>
                      <div>
                        <CardTitle className="text-sm">{agent.name}</CardTitle>
                        <CardDescription className="text-xs">{agent.role}</CardDescription>
                      </div>
                    </div>
                    {agent.unreadCount > 0 && (
                      <Badge className="bg-primary">{agent.unreadCount}</Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {agent.lastMessage}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {agent.lastActive}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Team Channels Section */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center">
            <Hash className="mr-2 h-5 w-5" />
            Team Channels
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filteredChannels.map((channel) => (
              <Card 
                key={channel.channelId} 
                className={`cursor-pointer hover:shadow-md transition-shadow ${channel.unreadCount > 0 ? 'border-primary/50' : ''}`}
                onClick={() => handleChannelClick(channel.channelId)}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <span>{channel.emoji}</span>
                      {channel.name}
                      {channel.unreadCount > 0 && (
                        <Badge className="bg-primary">{channel.unreadCount}</Badge>
                      )}
                    </CardTitle>
                    <span className="text-xs text-muted-foreground">
                      {channel.lastActive}
                    </span>
                  </div>
                  <CardDescription>
                    {channel.purpose}
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="text-sm">
                    <span className="font-medium">{channel.lastSender}: </span>
                    <span className="text-muted-foreground line-clamp-1">{channel.lastMessage}</span>
                  </div>
                </CardContent>
                
                <CardFooter className="pt-0">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="ml-auto flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChannelClick(channel.channelId);
                    }}
                  >
                    View Channel
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Chat View (both channel and agent)
  return (
    <div className="container mx-auto p-6 h-[calc(100vh-4rem)] flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 pb-4 border-b">
        <div className="flex items-center">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={goBack}
            className="mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          
          <div className="flex items-center gap-3">
            {view === 'agent' ? (
              <>
                <div className="relative">
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={sampleAgents.find(a => a.id === selectedAgent)?.avatar} />
                    <AvatarFallback>
                      {sampleAgents.find(a => a.id === selectedAgent)?.name.slice(0, 2)}
                    </AvatarFallback>
                  </Avatar>
                  <div className={`absolute -bottom-1 -right-1 h-3 w-3 rounded-full border-2 border-background ${getStatusColor(sampleAgents.find(a => a.id === selectedAgent)?.status || 'offline')}`} />
                </div>
                <div>
                  <div className="font-bold flex items-center gap-2">
                    {sampleAgents.find(a => a.id === selectedAgent)?.name}
                    <Badge variant="outline" className="text-xs">
                      {sampleAgents.find(a => a.id === selectedAgent)?.role}
                    </Badge>
                    <Badge className={`text-xs ${sampleAgents.find(a => a.id === selectedAgent)?.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`}>
                      {sampleAgents.find(a => a.id === selectedAgent)?.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground">Last active: Just now</div>
                </div>
              </>
            ) : (
              <>
                <span className="text-2xl">{channelInfo?.emoji}</span>
                <div>
                  <h1 className="text-xl font-bold">{channelInfo?.name}</h1>
                  <p className="text-sm text-muted-foreground">
                    {channels.find(c => c.channelId === selectedChannel)?.purpose}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        
        <div>
          <Button variant="outline" size="sm">
            {view === 'agent' ? 'View Profile' : 'Channel Info'}
          </Button>
        </div>
      </div>
      
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto px-4 py-4 bg-background/80 rounded-lg">
        {loading ? (
          <div className="text-center text-muted-foreground py-8">
            Loading messages...
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-muted-foreground py-8">
            {view === 'agent' ? 'Start a conversation with this agent' : 'No messages in this channel yet'}
          </div>
        ) : (
          messages.map((message) => (
            <ChatMessageComponent key={message.id} message={message} />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Message Input */}
      <div className="mt-4">
        <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }} className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="rounded-full"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          
          <Input
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            placeholder={`Message ${view === 'agent' ? sampleAgents.find(a => a.id === selectedAgent)?.name : `#${channelInfo?.name}`}...`}
            className="flex-1"
          />
          
          <Button
            type="submit"
            disabled={!messageInput.trim() || sendingMessage}
          >
            <Send className="h-4 w-4 mr-2" />
            Send
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AgentInsightsHub;